import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { emitFlashcardReviewRecorded } from '../../events/sessionEvents';
import { api } from '../../services/api';
import { DeckFlashcard } from '../../types';
import MarkdownContent from '../shared/MarkdownContent';
import Navbar from '../shared/Navbar';

type FlashcardReviewItem = DeckFlashcard & {
  isRepeat?: boolean;
};

const FlashcardReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawCourseId = searchParams.get('courseId');
  const parsedCourseId = rawCourseId ? Number(rawCourseId) : undefined;
  const courseId = parsedCourseId && Number.isInteger(parsedCourseId) && parsedCourseId > 0
    ? parsedCourseId
    : undefined;
  const sessionTitle = searchParams.get('courseTitle')?.trim()
    || (courseId ? 'Course flashcards' : 'All flashcards');

  const [flashcards, setFlashcards] = React.useState<FlashcardReviewItem[]>([]);
  const [initialFlashcardCount, setInitialFlashcardCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [progressCount, setProgressCount] = React.useState(0);
  const [isRevealed, setIsRevealed] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);

  React.useEffect(() => {
    const loadSession = async () => {
      setLoading(true);
      setError(null);
      if (rawCourseId !== null && courseId == null) {
        setError('Invalid course ID');
        setLoading(false);
        return;
      }
      try {
        const data = await api.getRevisionFlashcards(courseId);
        const deckFlashcards = data.flashcards || [];
        setFlashcards(deckFlashcards);
        setInitialFlashcardCount(deckFlashcards.length);
        setProgressCount(deckFlashcards.length > 0 ? 1 : 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load flashcards');
      } finally {
        setLoading(false);
      }
    };

    void loadSession();
  }, [courseId, rawCourseId]);

  const currentCard = flashcards[currentIndex];
  const progressText = initialFlashcardCount > 0
    ? `${Math.min(progressCount, initialFlashcardCount)} of ${initialFlashcardCount}`
    : '0 of 0';
  const progressPercent = initialFlashcardCount > 0
    ? (Math.min(progressCount, initialFlashcardCount) / initialFlashcardCount) * 100
    : 0;

  const handleRating = (rating: 'wrong' | 'tough' | 'easy') => {
    if (currentCard?.id) {
      const flashcardId = currentCard.id;
      void api.recordFlashcardResponse(flashcardId, rating)
        .then(() => emitFlashcardReviewRecorded(flashcardId))
        .catch(responseError => {
          console.error('Failed to record flashcard response:', responseError);
        });
    }

    if (rating === 'wrong' && currentCard) {
      setFlashcards(cards => [...cards, { ...currentCard, isRepeat: true }]);
      setCurrentIndex(index => index + 1);
      setIsRevealed(false);
      return;
    }

    setProgressCount(count => Math.min(count + 1, initialFlashcardCount));
    if (currentIndex >= flashcards.length - 1) {
      setIsComplete(true);
      return;
    }
    setCurrentIndex(index => index + 1);
    setIsRevealed(false);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setProgressCount(initialFlashcardCount > 0 ? 1 : 0);
    setIsRevealed(false);
    setIsComplete(false);
    setFlashcards(cards => cards.filter(card => !card.isRepeat));
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="flashcards-page">
          <div className="flashcards-study-header">
            <div>
              <h2>{sessionTitle}</h2>
              <p>Review the cards in this session.</p>
            </div>
            <button type="button" className="tm-button tm-button--secondary flashcards-back-btn" onClick={() => navigate('/flashcards')}>
              ← Back to choices
            </button>
          </div>

          {!loading && !error && initialFlashcardCount > 0 && !isComplete && (
            <div className="flashcards-progress-row" aria-label={`Progress: ${progressText}`}>
              <div className="flashcards-progress-track">
                <div
                  className="flashcards-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flashcards-progress-label">{progressText}</div>
            </div>
          )}

          {loading && <div className="tm-surface flashcards-study-card">Loading flashcards...</div>}
          {error && (
            <div className="tm-surface flashcards-study-card flashcards-study-error">
              {error === 'Authentication required'
                ? 'Sign in to revise your flashcard deck.'
                : error}
            </div>
          )}

          {!loading && !error && initialFlashcardCount === 0 && (
            <div className="tm-surface flashcards-study-card flashcards-study-empty">
              <h3>No flashcards in this deck yet</h3>
              <p>Add flashcards from the course page before starting revision.</p>
            </div>
          )}

          {!loading && !error && initialFlashcardCount > 0 && isComplete && (
            <div className="tm-surface flashcards-study-card flashcards-study-complete">
              <h3>Congratulations!</h3>
              <p>You have revised every flashcard in this session.</p>
              <div className="flashcards-complete-actions">
                <button type="button" className="tm-button tm-button--primary flashcards-primary-btn" onClick={handleRestart}>
                  Revise Again
                </button>
                <button type="button" className="tm-button tm-button--secondary flashcards-back-btn" onClick={() => navigate('/flashcards')}>
                  Choose another deck
                </button>
              </div>
            </div>
          )}

          {!loading && !error && currentCard && !isComplete && (
            <div className="tm-surface flashcards-study-card">
              <div className="flashcards-card-context">
                {currentCard.lesson_title} · {currentCard.section_name}
              </div>
              <div className="flashcards-card-face">
                <div className="flashcards-face-label">Question</div>
                <MarkdownContent content={currentCard.top_text || ''} />
              </div>

              {isRevealed && (
                <div className="flashcards-card-face flashcards-card-face-bottom flashcards-answer-reveal">
                  <div className="flashcards-face-label">Answer</div>
                  <MarkdownContent content={currentCard.bottom_text || ''} />
                </div>
              )}

              <div className="flashcards-study-actions">
                {!isRevealed ? (
                  <button
                    type="button"
                    className="tm-button tm-button--primary flashcards-primary-btn flashcards-reveal-btn"
                    onClick={() => setIsRevealed(true)}
                  >
                    Reveal Answer
                  </button>
                ) : (
                  <div className="flashcards-rating-actions" aria-label="How did that flashcard go?">
                    <button
                      type="button"
                      className="flashcards-rating-btn flashcards-rating-wrong"
                      onClick={() => handleRating('wrong')}
                    >
                      I got that wrong
                    </button>
                    <button
                      type="button"
                      className="flashcards-rating-btn flashcards-rating-tough"
                      onClick={() => handleRating('tough')}
                    >
                      That was tough
                    </button>
                    <button
                      type="button"
                      className="flashcards-rating-btn flashcards-rating-easy"
                      onClick={() => handleRating('easy')}
                    >
                      That was easy
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FlashcardReviewPage;
